// DripEditorReducer.ts — single source of truth for the in-progress drip
// campaign's working copy (steps, name, stopOnReply, timing).
//
// This slice is the NEW HOME for the shared drip working-copy types/helpers
// that used to live inline in wizard.tsx / drip-message-editor.tsx. Moving
// them here lets both surfaces import from one place without a circular
// dependency (wizard.tsx -> drip-message-editor.tsx -> wizard.tsx).
//
// PURITY: reducers must be pure, so no Date.now() inside a reducer body. The
// slice keeps a monotonic `seq` counter and mints bridge ids deterministically
// as `drip-step-${state.seq}` while incrementing `state.seq`. The Date.now()
// -based `nextBridgeId`/`emptyTemplate` below remain for use OUTSIDE reducers
// (e.g. component-side defaults, hydrating a fresh draft) — the reducer path
// never calls them.

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { Template } from '@common/models/template';
import type { CampaignRandomGap } from '@bt-common/schemas/bt/campaign';
import type { DelayGap } from '../../extension/context/content/components/campaigns-redesign/primitives';

// ════════ SHARED TYPES (moved from wizard.tsx / drip-message-editor.tsx) ════════

// A drip step template carries a synthetic, stable bridgeId in `_id` so the
// reused editor's currentTemplate._id-keyed reseed fires when switching steps.
// The id is stripped at build time (drip steps don't persist as saved templates).
export type DripStepTemplate = Partial<Template> & { _id: string };

export const newStepTemplate = (bridgeId: string): DripStepTemplate => ({
  _id: bridgeId,
  message: '',
});

// Each step's `template` is the editor-model `Template` (the shape the reused
// editor reads/writes) carrying a synthetic stable `_id` (bridgeId) used to key
// the singleton editor's reseed when switching steps. `gapAfter` is null on the
// last step in-UI; the schema strips it at build.
export interface UiStep {
  stepIndex: number;
  template: DripStepTemplate;
  gapAfter: DelayGap | null;
}

export interface TimingState {
  launch: 'now' | 'schedule';
  /** ISO datetime the first message should go out when launch==='schedule'.
   *  Bridged from the schedule picker (ScheduledMessageEditorReducer.dueDate) and
   *  persisted to the drip's `schedule.dueDate`. Undefined ⇒ send immediately. */
  scheduledAt?: string;
  /** FE#240: the campaign repeats on a cadence. Bridged from the schedule
   *  picker's recurrence UI (ScheduledMessageEditorReducer.isRecurring) and
   *  persisted to `schedule.isRecurring`. Only meaningful when launch==='schedule'
   *  AND `rruleset` is set — the ORCH#102 relaunch engine needs both. */
  isRecurring?: boolean;
  /** FE#240: the serialized RRULE the ORCH#102 relaunch engine re-fires on.
   *  Bridged from ScheduledMessageEditorReducer.rruleset; persisted to
   *  `schedule.rruleset`. */
  rruleset?: string;
  /** #412: opt-in randomized gap between messages. Absent = the fixed 7s default.
   *  Persisted on the drip as `randomGap` (null clears it). */
  randomGap?: CampaignRandomGap;
}

export const defaultGap = (): DelayGap => ({ value: 2, unit: 'hour' });

// Impure bridge-id generator — for use OUTSIDE reducers only (component-side
// defaults / hydrate-from-persisted-draft code paths). Never call from within
// a reducer body; use the `seq`-based id there instead.
let bridgeSeq = 0;
export const nextBridgeId = (): string => `drip-step-${Date.now()}-${bridgeSeq++}`;
export const emptyTemplate = (): DripStepTemplate => newStepTemplate(nextBridgeId());

// ════════ SLICE STATE ════════

export interface DripEditorState {
  dripId: string | null;
  steps: UiStep[];
  name: string;
  stopOnReply: boolean;
  timing: TimingState;
  hydrated: boolean;
  /** Monotonic counter for pure, in-reducer bridge-id generation. */
  seq: number;
}

// stopOnReply defaults OFF. The engine registers a "reply" for ANY inbound
// message in a chat, so for a GROUP recipient any member's post exits that
// group from the sequence — silently, for every remaining step. Broadcast-to-
// groups is the common case, so an on-by-default exit quietly shreds audiences.
// Opt in per campaign instead.
const initialState: DripEditorState = {
  dripId: null,
  steps: [],
  name: '',
  stopOnReply: false,
  timing: { launch: 'now' },
  hydrated: false,
  seq: 0,
};

export interface HydratePayload {
  dripId: string | null;
  steps: UiStep[];
  name: string;
  stopOnReply: boolean;
  timing: TimingState;
}

const DripEditorSlice = createSlice({
  name: 'DripEditor',
  initialState,
  reducers: {
    hydrate(state, action: PayloadAction<HydratePayload>) {
      const { dripId, steps, name, stopOnReply, timing } = action.payload;
      state.dripId = dripId;
      state.steps = steps;
      state.name = name;
      state.stopOnReply = stopOnReply;
      state.timing = timing;
      state.hydrated = true;
    },

    updateStepTemplate(state, action: PayloadAction<{ idx: number; template: DripStepTemplate }>) {
      const { idx, template } = action.payload;
      if (idx < 0 || idx >= state.steps.length) return;
      state.steps[idx].template = template;
    },

    updateStepGap(state, action: PayloadAction<{ idx: number; gap: DelayGap }>) {
      const { idx, gap } = action.payload;
      if (idx < 0 || idx >= state.steps.length) return;
      state.steps[idx].gapAfter = gap;
    },

    // single→multi: give the previous last step a gap, push a new gapless step.
    addFollowup(state) {
      const prev = state.steps;
      if (prev.length > 0) {
        const last = prev[prev.length - 1];
        last.gapAfter = last.gapAfter ?? defaultGap();
      }
      const id = `drip-step-${state.seq}`;
      state.seq += 1;
      state.steps.push({ stepIndex: prev.length, template: newStepTemplate(id), gapAfter: null });
    },

    removeStep(state, action: PayloadAction<{ idx: number }>) {
      const { idx } = action.payload;
      if (idx < 0 || idx >= state.steps.length) return;
      state.steps.splice(idx, 1);
      state.steps.forEach((s, j) => {
        s.stepIndex = j;
      });
      if (state.steps.length) {
        state.steps[state.steps.length - 1].gapAfter = null;
      }
    },

    moveStep(state, action: PayloadAction<{ idx: number; dir: number }>) {
      const { idx, dir } = action.payload;
      const j = idx + dir;
      if (j < 0 || j >= state.steps.length) return;
      const tmp = state.steps[idx];
      state.steps[idx] = state.steps[j];
      state.steps[j] = tmp;
      const total = state.steps.length;
      state.steps.forEach((s, k) => {
        s.stepIndex = k;
        s.gapAfter = k === total - 1 ? null : (s.gapAfter ?? defaultGap());
      });
    },

    setName(state, action: PayloadAction<string>) {
      state.name = action.payload;
    },

    setStopOnReply(state, action: PayloadAction<boolean>) {
      state.stopOnReply = action.payload;
    },

    setTiming(state, action: PayloadAction<TimingState>) {
      state.timing = action.payload;
    },

    reset(): DripEditorState {
      return {
        dripId: null,
        steps: [],
        name: '',
        stopOnReply: false,
        timing: { launch: 'now' },
        hydrated: false,
        seq: 0,
      };
    },
  },
});

export const {
  hydrate,
  updateStepTemplate,
  updateStepGap,
  addFollowup,
  removeStep,
  moveStep,
  setName,
  setStopOnReply,
  setTiming,
  reset,
} = DripEditorSlice.actions;

export default DripEditorSlice.reducer;
